/**
 * Sieve — client application shell (§6, §11, §12), mode-aware.
 *
 * Serves TWO dailies that share one shell: the prime hunt and the easier
 * Multiples game (docs/SPEC-multiples). Each has its own puzzle, streak and
 * stats; the difference at the UI layer is a divisor banner and a
 * remainder-based reveal. Framework-free; accessibility is first-class.
 */

import { PRIME_COUNT, MISTAKE_BUDGET, BRAND } from '../game/constants';
import { GRID_SIZE } from '../engine/grid';
import { factorString, isPrime } from '../engine/primes';
import { divisionReveal } from '../engine/divisibility';
import {
  loadPrimeQueue,
  loadMultiplesQueue,
  puzzleForToday,
  puzzleById,
  localDateString,
  indexForDate,
  msUntilLocalMidnight,
  formatCountdown,
  type Queue,
  type PrimeQueue,
  type MultiplesQueue,
} from '../game/queue';
import { loadState, saveState } from '../game/persistence';
import {
  defaultState,
  normalizePrime,
  normalizeMultiples,
  type PersistedState,
  type ModeState,
  type GameMode,
  type GameState,
  type PlayPuzzle,
} from '../game/types';
import { createGame, applyTap, replay, outcomeOf, isTargetCell } from '../game/state';
import { applyDailyResult, recordMistakes, winPercent, flawlessPercent } from '../game/stats';
import { shareFromGame, buildRapidShareText, shareOrCopy } from '../game/share';
import { startRapid, answer, type RapidState } from '../game/rapid';
import { dict, resolveLang, t, outcomeMessageFor, formatDate, SUPPORTED, DICTS, type Dict, type LangCode } from '../i18n';
import { h, mount, clear, trapFocus } from './dom';

type View = 'daily' | 'archive';

export class App {
  private root: HTMLElement;
  private modalRoot: HTMLElement;
  private liveRegion!: HTMLElement;
  private main!: HTMLElement;

  private state: PersistedState = defaultState();
  private primeQueue!: PrimeQueue;
  private multQueue!: MultiplesQueue;
  private d!: Dict;
  private lang!: LangCode;

  private gameMode: GameMode = 'multiples';
  private view: View = 'daily';
  private game: GameState | null = null;
  private cellEls: HTMLButtonElement[] = [];
  private focusIndex = 0;
  private countdownTimer: number | null = null;

  constructor(root: HTMLElement) {
    this.root = root;
    this.modalRoot = h('div', { id: 'modal-root' });
  }

  async init(): Promise<void> {
    this.state = await loadState();
    [this.primeQueue, this.multQueue] = await Promise.all([loadPrimeQueue(), loadMultiplesQueue()]);
    this.gameMode = this.state.lastMode;
    this.applyLanguage();
    this.applySettingsToDom();
    this.renderShell();
    this.startDaily();

    // First-run onboarding for the mode we landed in (§6.5).
    const totalPlays = this.state.prime.stats.played + this.state.multiples.stats.played;
    const hasResults = Object.keys(this.modeState().dailyResults).length > 0;
    if (totalPlays === 0 && !hasResults) this.openOnboarding();
  }

  // ─── Mode / state helpers ────────────────────────────────────────────────────

  private modeState(mode: GameMode = this.gameMode): ModeState {
    return this.state[mode];
  }

  /** Today's puzzle for the active mode, normalised for the game layer. */
  private todayPuzzle(mode: GameMode = this.gameMode): PlayPuzzle {
    return mode === 'multiples'
      ? normalizeMultiples(puzzleForToday(this.multQueue))
      : normalizePrime(puzzleForToday(this.primeQueue));
  }

  private puzzleByIdNormalized(mode: GameMode, id: number): PlayPuzzle | undefined {
    if (mode === 'multiples') {
      const p = puzzleById(this.multQueue, id);
      return p && normalizeMultiples(p);
    }
    const p = puzzleById(this.primeQueue, id);
    return p && normalizePrime(p);
  }

  private todayIndex(mode: GameMode = this.gameMode): number {
    const q: Queue = mode === 'multiples' ? this.multQueue : this.primeQueue;
    return indexForDate(q, localDateString());
  }

  // ─── Language & settings ─────────────────────────────────────────────────────

  private applyLanguage(): void {
    this.lang = resolveLang(this.state.settings.language);
    this.d = dict(this.state.settings.language);
    document.documentElement.lang = this.lang;
    document.documentElement.dir = this.d.meta.dir;
  }

  private applySettingsToDom(): void {
    const c = document.documentElement.classList;
    c.toggle('high-contrast', this.state.settings.highContrast);
    c.toggle('color-blind', this.state.settings.colorBlind);
  }

  private async persist(): Promise<void> {
    await saveState(this.state);
  }

  private announce(msg: string): void {
    if (this.liveRegion) this.liveRegion.textContent = msg;
  }

  // ─── Shell ───────────────────────────────────────────────────────────────────

  private renderShell(): void {
    this.liveRegion = h('div', { class: 'sr-only', 'aria-live': 'assertive', 'aria-atomic': 'true', role: 'status' });
    this.main = h('main', { id: 'screen' });

    const header = h(
      'header',
      { class: 'app-header' },
      h('button', { class: 'icon-btn', 'aria-label': this.d.header.menu, onClick: () => this.openMenu() }, iconMenu()),
      h('h1', { class: 'brand' }, BRAND),
      h(
        'div',
        { class: 'header-actions' },
        h('button', { class: 'icon-btn', 'aria-label': this.d.header.language, onClick: () => this.openLanguage() }, iconGlobe()),
        h('button', { class: 'icon-btn', 'aria-label': this.d.header.stats, onClick: () => this.openStats() }, iconTrophy()),
        h('button', { class: 'icon-btn', 'aria-label': this.d.header.help, onClick: () => this.openHelp() }, iconHelp()),
      ),
    );
    mount(this.root, header, this.main, this.liveRegion, this.modalRoot);
  }

  private rerenderAll(): void {
    this.applyLanguage();
    this.renderShell();
    if (this.view === 'daily') this.startDaily(true);
    else this.renderPlayScreen();
  }

  // ─── Daily flow ──────────────────────────────────────────────────────────────

  private setMode(mode: GameMode): void {
    if (this.gameMode === mode && this.view === 'daily') {
      this.closeModal();
      return;
    }
    this.gameMode = mode;
    this.state.lastMode = mode;
    void this.persist();
    this.closeModal();
    this.startDaily();
  }

  private startDaily(preserveGame = false): void {
    this.view = 'daily';
    const puzzle = this.todayPuzzle();
    const ms = this.modeState();

    if (!preserveGame) {
      const completed = ms.dailyResults[puzzle.id];
      if (completed) this.game = replay(puzzle, completed);
      else if (ms.inProgress && ms.inProgress.puzzleId === puzzle.id) this.game = replay(puzzle, ms.inProgress.taps);
      else this.game = createGame(puzzle);
    }
    this.renderPlayScreen();
    if (this.game && this.game.status !== 'playing') this.openEndModal();
  }

  private startArchive(mode: GameMode, id: number): void {
    const puzzle = this.puzzleByIdNormalized(mode, id);
    if (!puzzle) return;
    this.gameMode = mode;
    this.view = 'archive';
    const completed = this.modeState().archiveResults[id];
    this.game = completed ? replay(puzzle, completed) : createGame(puzzle);
    this.closeModal();
    this.renderPlayScreen();
    if (this.game.status !== 'playing') this.openEndModal();
  }

  // ─── Play screen (§6.2) ───────────────────────────────────────────────────────

  private renderPlayScreen(): void {
    const g = this.game!;
    const p = g.puzzle;
    const isMult = p.mode === 'multiples';
    const isArchive = this.view === 'archive';

    const counterLabel = isMult ? this.d.mult.found : this.d.play.primesLeft;
    const primePips = h(
      'div',
      { class: 'pips primes' },
      ...Array.from({ length: PRIME_COUNT }, (_, i) => h('span', { class: `pip ${i < g.found ? 'filled' : ''}` })),
    );
    const mistakePips = h(
      'div',
      { class: 'pips mistakes' },
      ...Array.from({ length: MISTAKE_BUDGET }, (_, i) => h('span', { class: `pip ${i < g.mistakes ? 'filled' : ''}` })),
    );
    const status = h(
      'div',
      { class: 'play-status' },
      h('div', { class: 'counter' }, h('span', { class: 'counter-label' }, counterLabel), primePips),
      h('div', { class: 'counter' }, h('span', { class: 'counter-label' }, this.d.play.mistakes), mistakePips),
    );

    // The divisor banner — always visible, part of the grid's accessible name.
    const banner = isMult
      ? h(
          'div',
          { class: 'divisor-banner', role: 'note' },
          h('strong', {}, t(this.d.mult.banner, { x: p.divisor! })),
          h('span', { class: 'find-all' }, ' — ', this.d.mult.findAll),
        )
      : null;

    this.cellEls = [];
    const gridLabel = isMult
      ? `${t(this.d.mult.banner, { x: p.divisor! })} — ${GRID_SIZE}×${GRID_SIZE}`
      : `${GRID_SIZE}×${GRID_SIZE} ${BRAND}`;
    const grid = h('div', { class: 'grid', role: 'grid', 'aria-label': gridLabel, style: `--n:${GRID_SIZE}` });
    p.grid.forEach((num, i) => {
      const cell = this.renderCell(num, i);
      this.cellEls.push(cell);
      grid.append(cell);
    });
    grid.addEventListener('keydown', (e) => this.onGridKey(e as KeyboardEvent));

    const head = h(
      'div',
      { class: 'play-head' },
      h('span', { class: 'puzzle-no' }, t(this.d.play.puzzleLabel, { id: p.id })),
      h('span', { class: 'puzzle-date' }, formatDate(p.date, this.lang)),
      isArchive ? h('span', { class: 'archive-badge' }, this.d.menu.archive) : null,
    );

    mount(this.main, head, status, banner, grid);
    this.focusIndex = this.firstPlayableIndex();
    this.updateRovingTabindex();
  }

  private firstPlayableIndex(): number {
    const g = this.game!;
    for (let i = 0; i < g.puzzle.grid.length; i++) if (!g.taps.includes(i)) return i;
    return 0;
  }

  /** Mode-aware reveal string beneath a cell (§6.3 / Multiples §6.3). */
  private reveal(num: number): string {
    const p = this.game!.puzzle;
    return p.mode === 'multiples' ? divisionReveal(num, p.divisor!) : factorString(num);
  }

  private renderCell(num: number, index: number): HTMLButtonElement {
    const g = this.game!;
    const p = g.puzzle;
    const isMult = p.mode === 'multiples';
    const over = g.status !== 'playing';
    const tapped = g.taps.includes(index);
    const isTarget = isTargetCell(p, index);

    let cls = 'cell';
    let sub: HTMLElement | null = null;
    let glyph = '';
    let aria = `${num}`;

    const targetWord = isMult ? t(this.d.mult.divisible, { x: p.divisor! }) : this.d.cell.prime;
    const nonTargetWord = isMult ? t(this.d.mult.notDivisible, { x: p.divisor! }) : this.d.cell.composite;

    if (tapped) {
      if (isTarget) {
        cls += ' correct';
        glyph = '✓';
        // Multiples show the product on a correct tap too; primes just tick.
        if (isMult) sub = h('span', { class: 'factor' }, this.reveal(num));
        aria = `${num}, ${targetWord}`;
      } else {
        cls += ' wrong';
        glyph = '✕';
        sub = h('span', { class: 'factor' }, this.reveal(num));
        aria = `${num}, ${nonTargetWord}, ${this.spokenReveal(num)}`;
      }
    } else if (over) {
      if (isTarget) {
        cls += ' revealed-prime';
        glyph = '○';
        if (isMult) sub = h('span', { class: 'factor' }, this.reveal(num));
        aria = `${num}, ${targetWord}`;
      } else {
        cls += ' revealed-composite';
        sub = h('span', { class: 'factor' }, this.reveal(num));
        aria = `${num}, ${nonTargetWord}`;
      }
    }

    return h(
      'button',
      {
        class: cls,
        role: 'gridcell',
        type: 'button',
        tabindex: '-1',
        'aria-label': aria,
        disabled: over || tapped,
        dataset: { index: String(index) },
        onClick: () => this.onCellTap(index),
      },
      h('span', { class: 'num' }, String(num)),
      glyph ? h('span', { class: 'glyph', 'aria-hidden': 'true' }, glyph) : null,
      sub,
    ) as HTMLButtonElement;
  }

  /** Spoken form of the reveal for the ARIA live region (§12). */
  private spokenReveal(num: number): string {
    const p = this.game!.puzzle;
    if (p.mode === 'multiples') {
      const r = num % p.divisor!;
      const q = Math.floor(num / p.divisor!);
      return r === 0
        ? `${p.divisor} times ${q}`
        : `${p.divisor} times ${q}, ${t(this.d.mult.remainder, { r })}`;
    }
    if (isPrime(num)) return '';
    return factorString(num).replace(/×/g, ' times ').replace(/²/g, ' squared').replace(/³/g, ' cubed');
  }

  private onCellTap(index: number): void {
    const g = this.game!;
    if (g.status !== 'playing' || g.taps.includes(index)) return;
    const p = g.puzzle;
    const num = p.grid[index]!;
    const correct = isTargetCell(p, index);
    applyTap(g, index);

    const fresh = this.renderCell(num, index);
    fresh.classList.add(correct ? 'anim-pop' : 'anim-shake');
    this.cellEls[index]!.replaceWith(fresh);
    this.cellEls[index] = fresh;

    const isMult = p.mode === 'multiples';
    const targetWord = isMult ? t(this.d.mult.divisible, { x: p.divisor! }) : this.d.cell.prime;
    const nonTargetWord = isMult ? t(this.d.mult.notDivisible, { x: p.divisor! }) : this.d.cell.composite;
    if (correct) this.announce(`${num}, ${targetWord}. ${g.found}/${PRIME_COUNT}.`);
    else this.announce(`${num}, ${nonTargetWord}, ${this.spokenReveal(num)}. ${MISTAKE_BUDGET - g.mistakes} ${this.d.play.mistakes}.`);

    this.refreshCounters();
    this.persistProgress();

    if (g.status !== 'playing') this.onGameEnd();
    else {
      this.focusIndex = this.firstPlayableIndex();
      this.updateRovingTabindex();
    }
  }

  private refreshCounters(): void {
    const g = this.game!;
    this.main.querySelectorAll('.pips.primes .pip').forEach((el, i) => el.classList.toggle('filled', i < g.found));
    this.main.querySelectorAll('.pips.mistakes .pip').forEach((el, i) => el.classList.toggle('filled', i < g.mistakes));
  }

  private onGameEnd(): void {
    const g = this.game!;
    const outcome = outcomeOf(g)!;
    const ms = this.modeState();

    if (this.view === 'daily' && ms.dailyResults[g.puzzle.id] === undefined) {
      const prevId = ms.lastCompletedDailyId;
      const consecutive = prevId === 0 ? true : g.puzzle.id === prevId + 1;
      ms.stats = applyDailyResult(ms.stats, outcome, consecutive);
      ms.stats = recordMistakes(ms.stats, outcome, g.mistakes);
      ms.dailyResults[g.puzzle.id] = [...g.taps];
      ms.lastCompletedDailyId = g.puzzle.id;
      ms.lastPuzzleId = g.puzzle.id;
      ms.inProgress = null;
    } else if (this.view === 'archive') {
      ms.archiveResults[g.puzzle.id] = [...g.taps];
    }
    void this.persist();

    this.renderPlayScreen();
    window.setTimeout(() => this.openEndModal(), 650);
    this.announce(this.d.outcome[outcome]);
  }

  private persistProgress(): void {
    const g = this.game!;
    if (this.view === 'daily' && g.status === 'playing') {
      const ms = this.modeState();
      ms.inProgress = { puzzleId: g.puzzle.id, taps: [...g.taps], mistakes: g.mistakes };
      ms.lastPuzzleId = g.puzzle.id;
      void this.persist();
    }
  }

  // ─── Keyboard navigation (§12) ────────────────────────────────────────────────

  private updateRovingTabindex(): void {
    this.cellEls.forEach((el, i) => el.setAttribute('tabindex', i === this.focusIndex ? '0' : '-1'));
  }

  private onGridKey(e: KeyboardEvent): void {
    const n = GRID_SIZE;
    let next = this.focusIndex;
    switch (e.key) {
      case 'ArrowRight': next = Math.min(this.focusIndex + 1, n * n - 1); break;
      case 'ArrowLeft': next = Math.max(this.focusIndex - 1, 0); break;
      case 'ArrowDown': next = Math.min(this.focusIndex + n, n * n - 1); break;
      case 'ArrowUp': next = Math.max(this.focusIndex - n, 0); break;
      case 'Home': next = 0; break;
      case 'End': next = n * n - 1; break;
      case 'Enter':
      case ' ':
        e.preventDefault();
        this.onCellTap(this.focusIndex);
        return;
      default: return;
    }
    e.preventDefault();
    this.focusIndex = next;
    this.updateRovingTabindex();
    this.cellEls[next]!.focus();
  }

  // ─── End modal (§6.4) ──────────────────────────────────────────────────────────

  private openEndModal(): void {
    const g = this.game!;
    const outcome = outcomeOf(g)!;
    const isDaily = this.view === 'daily';

    const shareBtn = h('button', { class: 'btn primary', onClick: (e: Event) => this.onShare(e.currentTarget as HTMLButtonElement) }, iconShare(), ' ', this.d.end.share);
    const countdownEl = h('span', { class: 'countdown' }, formatCountdown(msUntilLocalMidnight()));

    const body = h(
      'div',
      { class: 'end-body' },
      h('div', { class: `outcome-tier ${outcome}` }, this.d.outcome[outcome]),
      h('p', { class: 'outcome-msg' }, outcomeMessageFor(this.d, g.puzzle.mode, outcome, g.puzzle.id)),
      this.statsSummary(),
      shareBtn,
      isDaily ? h('div', { class: 'next-countdown' }, this.d.end.nextIn, ' ', countdownEl) : null,
      h(
        'div',
        { class: 'end-links' },
        h('button', { class: 'link', onClick: () => this.openArchive() }, this.d.end.archive),
        h('button', { class: 'link rapid-link', onClick: () => this.openRapid() }, this.d.end.keepGoing),
      ),
    );
    this.openModal(this.d.end.yourResult, body, () => this.closeModal());
    if (isDaily) this.startCountdown(countdownEl);
  }

  private statsSummary(): HTMLElement {
    const s = this.modeState().stats;
    const cell = (label: string, value: string | number) =>
      h('div', { class: 'stat' }, h('div', { class: 'stat-val' }, String(value)), h('div', { class: 'stat-label' }, label));
    return h(
      'div',
      { class: 'stats-grid compact' },
      cell(this.d.stats.played, s.played),
      cell(this.d.stats.winPct, winPercent(s)),
      cell(this.d.stats.currentStreak, s.currentStreak),
      cell(this.d.stats.maxStreak, s.maxStreak),
    );
  }

  private startCountdown(el: HTMLElement): void {
    if (this.countdownTimer) window.clearInterval(this.countdownTimer);
    this.countdownTimer = window.setInterval(() => {
      const ms = msUntilLocalMidnight();
      el.textContent = formatCountdown(ms);
      if (ms <= 0 && this.countdownTimer) {
        window.clearInterval(this.countdownTimer);
        this.closeModal();
        this.startDaily();
      }
    }, 1000);
  }

  private async onShare(btn: HTMLButtonElement): Promise<void> {
    const g = this.game!;
    const streak = this.view === 'daily' ? this.modeState().stats.currentStreak : 0;
    const result = await shareOrCopy(shareFromGame(g, streak), BRAND);
    const label = result === 'shared' ? this.d.end.shared : result === 'copied' ? this.d.end.copied : this.d.end.shareFailed;
    const original = btn.innerHTML;
    btn.textContent = label;
    window.setTimeout(() => (btn.innerHTML = original), 1600);
  }

  // ─── Menu drawer ────────────────────────────────────────────────────────────

  private openMenu(): void {
    const item = (label: string, onClick: () => void, extra?: string) =>
      h('button', { class: `menu-item ${this.gameMode ? '' : ''}`, onClick }, label, extra ? h('span', { class: 'menu-tag' }, extra) : null);
    const body = h(
      'nav',
      { class: 'menu' },
      item(this.d.mult.menuToday, () => this.setMode('multiples')),
      item(this.d.mult.primeToday, () => this.setMode('prime'), this.d.mult.harder),
      h('hr', {}),
      item(this.d.menu.rapid, () => this.openRapid()),
      item(this.d.menu.archive, () => this.openArchive()),
      item(this.d.menu.stats, () => this.openStats()),
      item(this.d.menu.settings, () => this.openSettings()),
      item(this.d.menu.help, () => this.openHelp()),
      h('hr', {}),
      item(this.d.menu.privacy, () => window.open('privacy.html', '_blank')),
      item(this.d.menu.terms, () => window.open('terms.html', '_blank')),
      item(this.d.menu.attributions, () => window.open('attributions.html', '_blank')),
    );
    this.openModal(this.d.menu.title, body, () => this.closeModal());
  }

  // ─── Language dropdown (§6.1) ─────────────────────────────────────────────────

  private openLanguage(): void {
    const options = [
      h('button', { class: `menu-item ${this.state.settings.language === 'auto' ? 'active' : ''}`, onClick: () => this.setLanguage('auto') }, this.d.settings.auto),
      ...SUPPORTED.map((code) =>
        h('button', { class: `menu-item ${this.state.settings.language === code ? 'active' : ''}`, onClick: () => this.setLanguage(code) }, DICTS[code].meta.name),
      ),
    ];
    this.openModal(this.d.header.language, h('nav', { class: 'menu' }, ...options), () => this.closeModal());
  }

  private setLanguage(lang: 'auto' | LangCode): void {
    this.state.settings.language = lang;
    void this.persist();
    this.closeModal();
    this.rerenderAll();
  }

  // ─── Stats (§8) ────────────────────────────────────────────────────────────────

  private openStats(): void {
    const s = this.modeState().stats;
    const modeName = this.gameMode === 'multiples' ? this.d.mult.menuToday : this.d.mult.primeToday;
    const stat = (label: string, value: string | number) =>
      h('div', { class: 'stat' }, h('div', { class: 'stat-val' }, String(value)), h('div', { class: 'stat-label' }, label));

    const grid = h(
      'div',
      { class: 'stats-grid' },
      stat(this.d.stats.played, s.played),
      stat(this.d.stats.winPct, winPercent(s)),
      stat(this.d.stats.currentStreak, s.currentStreak),
      stat(this.d.stats.maxStreak, s.maxStreak),
      stat(this.d.stats.flawless, s.flawless),
      stat(this.d.stats.flawlessPct, flawlessPercent(s)),
      stat(this.d.stats.rapidBest, this.state.rapidBest),
    );

    const maxBar = Math.max(1, ...s.mistakeHistogram);
    const labels = [this.d.stats.buckets['0'], this.d.stats.buckets['1'], this.d.stats.buckets['2'], this.d.stats.buckets.failed];
    const histo = h(
      'div',
      { class: 'histogram' },
      h('h3', {}, this.d.stats.distribution),
      ...s.mistakeHistogram.map((count, i) =>
        h(
          'div',
          { class: 'histo-row' },
          h('span', { class: 'histo-label' }, labels[i]!),
          h('div', { class: 'histo-bar-wrap' }, h('div', { class: `histo-bar ${i === 3 ? 'fail' : ''}`, style: `width:${(count / maxBar) * 100}%` }, count > 0 ? String(count) : '')),
        ),
      ),
    );

    const body = h(
      'div',
      {},
      h('div', { class: 'stats-mode' }, modeName),
      s.played === 0 ? h('div', { class: 'empty' }, this.d.stats.none) : h('div', {}, grid, histo),
    );
    this.openModal(this.d.stats.title, body, () => this.closeModal());
  }

  // ─── Help / onboarding (§6.5) ─────────────────────────────────────────────────

  private openHelp(): void {
    const cards = h(
      'div',
      { class: 'help-cards' },
      // Multiples help (the default daily) then the prime cards + cheat sheet.
      this.helpCard('M', this.d.mult.onboard1Title, this.d.mult.onboard1Body),
      this.helpCard(1, this.d.help.card1Title, this.d.help.card1Body),
      this.helpCard(2, this.d.help.card2Title, this.d.help.card2Body),
      this.helpCard(3, this.d.help.card3Title, this.d.help.card3Body),
      this.cheatSheet(),
    );
    this.openModal(this.d.help.title, cards, () => this.closeModal());
  }

  private helpCard(n: number | string, title: string, body: string): HTMLElement {
    return h('section', { class: 'help-card' }, h('span', { class: 'card-num' }, String(n)), h('h3', {}, title), h('p', {}, body));
  }

  private cheatSheet(): HTMLElement {
    return h('section', { class: 'cheat' }, h('h3', {}, this.d.help.cheatTitle), h('ul', {}, ...this.d.help.cheat.map((line) => h('li', {}, line))));
  }

  private openOnboarding(): void {
    // Mode-appropriate onboarding: Multiples = 2 cards, Sieve = 3 cards (§6.5).
    const cards =
      this.gameMode === 'multiples'
        ? [
            { n: 1, title: this.d.mult.onboard1Title, body: this.d.mult.onboard1Body },
            { n: 2, title: this.d.mult.onboard2Title, body: this.d.mult.onboard2Body },
          ]
        : [
            { n: 1, title: this.d.help.card1Title, body: this.d.help.card1Body },
            { n: 2, title: this.d.help.card2Title, body: this.d.help.card2Body },
            { n: 3, title: this.d.help.card3Title, body: this.d.help.card3Body },
          ];
    let idx = 0;
    const container = h('div', { class: 'onboarding' });
    const render = () => {
      const isLast = idx === cards.length - 1;
      const c = cards[idx]!;
      mount(
        container,
        this.helpCard(c.n, c.title, c.body),
        h('div', { class: 'dots' }, ...cards.map((_, i) => h('span', { class: `dot ${i === idx ? 'on' : ''}` }))),
        h(
          'div',
          { class: 'onboard-actions' },
          h('button', { class: 'link', onClick: () => this.closeModal() }, this.d.help.skip),
          h('button', { class: 'btn primary', onClick: () => { if (isLast) this.closeModal(); else { idx++; render(); } } }, isLast ? this.d.help.start : this.d.help.next),
        ),
      );
    };
    render();
    this.openModal(this.d.brand, container, () => this.closeModal());
  }

  // ─── Settings (§12) ────────────────────────────────────────────────────────────

  private openSettings(): void {
    const toggle = (label: string, key: 'highContrast' | 'colorBlind') =>
      h(
        'label',
        { class: 'setting-row' },
        h('span', {}, label),
        h('input', {
          type: 'checkbox',
          checked: this.state.settings[key],
          onChange: (e: Event) => {
            this.state.settings[key] = (e.target as HTMLInputElement).checked;
            this.applySettingsToDom();
            void this.persist();
          },
        }),
      );
    this.openModal(this.d.settings.title, h('div', { class: 'settings' }, toggle(this.d.settings.highContrast, 'highContrast'), toggle(this.d.settings.colorBlind, 'colorBlind')), () => this.closeModal());
  }

  // ─── Archive (§8) — for the active mode ────────────────────────────────────────

  private openArchive(): void {
    const mode = this.gameMode;
    const ms = this.modeState(mode);
    const q = mode === 'multiples' ? this.multQueue : this.primeQueue;
    const todayIdx = this.todayIndex(mode);
    const rows: HTMLElement[] = [];
    for (let i = todayIdx; i >= 0; i--) {
      const p = q.puzzles[i]!;
      const done = ms.archiveResults[p.id] !== undefined || ms.dailyResults[p.id] !== undefined;
      const tag = 'divisor' in p ? `÷${(p as { divisor: number }).divisor}` : '';
      rows.push(
        h(
          'button',
          { class: 'archive-row', onClick: () => this.startArchive(mode, p.id) },
          h('span', { class: 'archive-no' }, `#${p.id}`),
          h('span', { class: 'archive-date' }, formatDate(p.date, this.lang)),
          h('span', { class: 'archive-tier' }, tag || ('difficultyTier' in p ? String((p as { difficultyTier: string }).difficultyTier).toUpperCase() : '')),
          done ? h('span', { class: 'archive-done' }, this.d.archive.done) : h('span', { class: 'archive-play' }, this.d.archive.play),
        ),
      );
    }
    const body = h('div', { class: 'archive' }, h('p', { class: 'archive-sub' }, this.d.archive.subtitle), h('div', { class: 'archive-list' }, ...rows));
    this.openModal(this.d.archive.title, body, () => this.closeModal());
  }

  // ─── Rapid mode (§10) ──────────────────────────────────────────────────────────

  private openRapid(): void {
    const plays = this.state.prime.stats.played + this.state.multiples.stats.played;
    const seed = ((this.state.rapidBest + 1) * 2654435761 + plays * 40503 + Date.now()) >>> 0;
    let rapid: RapidState = startRapid(this.state.rapidBest, seed);

    const container = h('div', { class: 'rapid' });
    const render = () => {
      if (!rapid.over) {
        mount(
          container,
          h('p', { class: 'rapid-sub' }, this.d.rapid.subtitle),
          h('div', { class: 'rapid-number', 'aria-live': 'polite' }, String(rapid.current)),
          h(
            'div',
            { class: 'rapid-btns' },
            h('button', { class: 'btn big prime', onClick: () => { rapid = answer(rapid, true); this.afterRapid(rapid); render(); } }, this.d.rapid.prime),
            h('button', { class: 'btn big not-prime', onClick: () => { rapid = answer(rapid, false); this.afterRapid(rapid); render(); } }, this.d.rapid.notPrime),
          ),
          h('div', { class: 'rapid-scores' }, h('span', {}, `${this.d.rapid.streak}: ${rapid.streak}`), h('span', {}, `${this.d.rapid.best}: ${rapid.best}`)),
        );
      } else {
        mount(
          container,
          h('div', { class: 'outcome-tier failed' }, this.d.rapid.gameOver),
          h('p', { class: 'rapid-miss' }, `${rapid.lastMiss!.number} ${this.d.rapid.was} ${rapid.lastMiss!.factorisation}`),
          h('div', { class: 'rapid-final' }, `${this.d.rapid.streak}: ${rapid.streak} · ${this.d.rapid.best}: ${rapid.best}`),
          h(
            'div',
            { class: 'rapid-btns' },
            h('button', { class: 'btn primary', onClick: () => void shareOrCopy(buildRapidShareText(rapid.streak), BRAND) }, this.d.end.share),
            h('button', { class: 'btn', onClick: () => this.openRapid() }, this.d.rapid.playAgain),
          ),
          h('button', { class: 'link', onClick: () => { this.closeModal(); this.startDaily(); } }, this.d.rapid.back),
        );
      }
    };
    render();
    this.openModal(this.d.rapid.title, container, () => this.closeModal());
  }

  private afterRapid(rapid: RapidState): void {
    if (rapid.best > this.state.rapidBest) {
      this.state.rapidBest = rapid.best;
      void this.persist();
    }
  }

  // ─── Modal plumbing ────────────────────────────────────────────────────────────

  private disposeTrap: (() => void) | null = null;

  private openModal(title: string, body: HTMLElement, onClose: () => void): void {
    this.closeModal();
    const closeBtn = h('button', { class: 'icon-btn modal-close', 'aria-label': this.d.common.close, onClick: onClose }, iconClose());
    const modal = h(
      'div',
      { class: 'modal', role: 'dialog', 'aria-modal': 'true', 'aria-label': title },
      h('div', { class: 'modal-head' }, h('h2', {}, title), closeBtn),
      h('div', { class: 'modal-content' }, body),
    );
    const overlay = h('div', { class: 'overlay', onClick: (e: Event) => { if (e.target === overlay) onClose(); } }, modal);
    mount(this.modalRoot, overlay);
    this.disposeTrap = trapFocus(modal, onClose);
    (modal.querySelector('button, [href], input') as HTMLElement | null)?.focus();
  }

  private closeModal(): void {
    if (this.countdownTimer) {
      window.clearInterval(this.countdownTimer);
      this.countdownTimer = null;
    }
    if (this.disposeTrap) {
      this.disposeTrap();
      this.disposeTrap = null;
    }
    clear(this.modalRoot);
  }
}

// ─── Inline SVG icons (self-contained, no external assets, §13.3) ─────────────
const svg = (path: string): SVGElement => {
  const el = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  el.setAttribute('viewBox', '0 0 24 24');
  el.setAttribute('width', '22');
  el.setAttribute('height', '22');
  el.setAttribute('fill', 'none');
  el.setAttribute('stroke', 'currentColor');
  el.setAttribute('stroke-width', '2');
  el.setAttribute('stroke-linecap', 'round');
  el.setAttribute('stroke-linejoin', 'round');
  el.setAttribute('aria-hidden', 'true');
  el.innerHTML = path;
  return el;
};
const iconMenu = () => svg('<line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/>');
const iconGlobe = () => svg('<circle cx="12" cy="12" r="9"/><line x1="3" y1="12" x2="21" y2="12"/><path d="M12 3a15 15 0 0 1 0 18a15 15 0 0 1 0-18"/>');
const iconTrophy = () => svg('<path d="M8 21h8M12 17v4M7 4h10v5a5 5 0 0 1-10 0V4zM7 6H4v2a3 3 0 0 0 3 3M17 6h3v2a3 3 0 0 1-3 3"/>');
const iconHelp = () => svg('<circle cx="12" cy="12" r="9"/><path d="M9.5 9a2.5 2.5 0 1 1 3.5 2.3c-.8.4-1 .9-1 1.7"/><line x1="12" y1="17" x2="12" y2="17"/>');
const iconClose = () => svg('<line x1="6" y1="6" x2="18" y2="18"/><line x1="18" y1="6" x2="6" y2="18"/>');
const iconShare = () => svg('<circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.6" y1="13.5" x2="15.4" y2="17.5"/><line x1="15.4" y1="6.5" x2="8.6" y2="10.5"/>');
